import React from 'react';
import { useFinancialPositionQuery } from '@/api';
import type { ErrorResponse } from '@/api/handlers/responses';
import type {
    FinancialPositionReadiness,
    FinancialPositionZone,
} from '@/api/models';

/**
 * The assessment rail's own read.
 *
 * Shares the query key with the four metric cards, so the rail and the cards are
 * always describing the same moment — and the screen still makes one request.
 */
export const useZoneRail = () => {
    const query = useFinancialPositionQuery();

    const zone = React.useMemo<FinancialPositionZone | null>(
        () => (query.data?.success ? query.data.response.zone : null),
        [query.data],
    );

    const readiness = React.useMemo<FinancialPositionReadiness | null>(
        () => (query.data?.success ? query.data.response.readiness : null),
        [query.data],
    );

    const error = React.useMemo<ErrorResponse | null>(
        () =>
            query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.data],
    );

    const onRetry = React.useCallback(() => {
        void query.refetch();
    }, [query]);

    return { zone, readiness, error, isLoading: query.isPending, onRetry };
};
